import React from 'react';
import styled from 'styled-components';
import { media } from '../theme/media';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import LeadModal from './quiz/LeadModal';
import { normalizeMediaUrl } from '../api/base';
import { safeRender } from '../utils/safeRender';

const Wrap = styled.div`
  position: relative;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: var(--shadow);
  background: #000;
`;

const Viewport = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 7;
  overflow: hidden;
  background: #111;
  min-height: clamp(220px, 72vw, 520px);
  ${media.tiny`
    aspect-ratio: auto;
    min-height: clamp(220px, 90vw, 380px);
  `}
`;

const Track = styled.div`
  display: flex;
  height: 100%;
  will-change: transform;
  transform: translate3d(${p => p.$tx || '0%'}, 0, 0);
  transition: transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  @media (prefers-reduced-motion: reduce){ transition: none; }
`;

const Slide = styled.div`
  position: relative;
  flex: 0 0 100%;
  height: 100%;
  background: #111;

  img{
    width:100%;
    height:100%;
    object-fit:cover;
    display:block;
  }
`;

const Caption = styled.div`
  position: absolute; 
  left: 22px;
  bottom: calc(24px + env(safe-area-inset-bottom));
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: fit-content;
  max-width: min(520px, calc(100% - 180px));

  h3{
    margin: 0;
    font-size: clamp(20px, 4.8vw, 32px);
    font-weight: 800;
    line-height: 1.12;
    color: #000;
    background: var(--gold);
    opacity: 0.92;
    padding: 12px 18px;
    border-radius: 14px;
    backdrop-filter: blur(12px);
    display: inline-block;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
    border: 1px solid rgba(255, 255, 255, 0.4);
    width: fit-content;
  }

  p{
    margin: 0;
    font-size: clamp(13px, 3.6vw, 18px);
    line-height: 1.4;
    color: #000;
    background: var(--gold);
    opacity: 0.88;
    padding: 10px 16px;
    border-radius: 12px;
    backdrop-filter: blur(10px);
    display: inline-block;
    box-shadow: 0 2px 15px rgba(0, 0, 0, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.3);
    width: fit-content;
  }

  ${media.tablet`
    max-width: calc(100% - 48px);
  `}
  ${media.tiny`
    bottom: calc(88px + env(safe-area-inset-bottom));
    left: 16px;
    max-width: calc(100% - 32px);
    gap: 6px;
    
    h3 {
      padding: 10px 14px;
      border-radius: 12px;
    }
    
    p {
      padding: 8px 12px;
      border-radius: 10px;
    }
  `}
`;

const CtaBtn = styled.button`
  position:absolute; right:18px; bottom: calc(20px + env(safe-area-inset-bottom));
  
  /* Стиль как у плашек в Caption */
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
  color: #000;
  background: var(--gold);
  opacity: 0.92;
  padding: 12px 18px;
  border-radius: 14px;
  backdrop-filter: blur(12px);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.4);
  cursor: pointer;
  z-index: 2;
  transition: opacity .2s ease, transform .2s ease;
  width: fit-content;

  &:hover { 
    opacity: 0.98;
    transform: translateY(-1px);
  }

  ${media.tiny`
      left: 22px;
      right: 22px;
      font-size: 13px;
      padding: 11px 16px;
      border-radius: 12px;
    `}
`;

const SlideLink = styled(Link)`
  position:absolute; inset:0; z-index:1;
`;

const NavBtn = styled.button`
  position:absolute; top:50%;
  ${(p)=>p.$left ? 'left:12px;' : 'right:12px;'}
  width:42px; height:42px; border-radius:12px;
  display:flex; align-items:center; justify-content:center;
  padding:0; box-sizing:border-box;
  background: rgba(255,255,255,.14);
  border:1px solid rgba(255,255,255,.25);
  color:#fff; line-height:0; font-size:0; user-select:none;
  backface-visibility:hidden; transform-style: preserve-3d;
  contain: layout paint;               /* изолируем от пересчётов соседних слоёв */
  -webkit-appearance: none; appearance: none;
  cursor:pointer; z-index:3; outline: none;
  transition: none;                    /* без анимации — исключаем «подпрыгивание» */
  &:hover{ background: rgba(255,255,255,.24); }
  &:active{ background: rgba(255,255,255,.28); }

  ${media.tiny`
    width: 36px;
    height: 36px;
    border-radius: 11px;
    left: ${p => (p.$left ? '8px' : 'auto')};
    right: ${p => (!p.$left ? '8px' : 'auto')};
  `}
`;

const Icon = styled.svg` width:18px; height:18px; display:block; `;
const ArrowLeftIcon = () => (
  <Icon viewBox="0 0 24 24" aria-hidden="true">
    <path d="M15 4.5L8 12l7 7.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Icon>
);
const ArrowRightIcon = () => (
  <Icon viewBox="0 0 24 24" aria-hidden="true">
    <path d="M9 4.5L16 12l-7 7.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Icon>
);

const Dots = styled.div`
  position:absolute; left:50%; transform:translateX(-50%);
  bottom: calc(14px + env(safe-area-inset-bottom));
  display:flex; gap:8px; z-index:3;
`;

const Dot = styled.button`
  width:24px; height:3px; border-radius:2px;
  border:0; padding:5px 5px; cursor:pointer; outline:none; transition: background .15s ease;
  background: ${(p)=>p.$active ? 'var(--gold)' : 'rgba(255,255,255,.4)'};
  opacity:${(p)=>p.$active ? 1 : .8};
`;

function extractKeyFromLink(link) {
  if (!link || typeof link !== 'string') return '';
  try {
    const url = new URL(link, typeof window !== 'undefined' ? window.location.origin : 'http://local');
    const parts = url.pathname.split('/').filter(Boolean);
    return parts[parts.length - 1] || '';
  } catch {
    const path = link.split('?')[0].split('#')[0];
    const parts = path.split('/').filter(Boolean);
    return parts[parts.length - 1] || '';
  }
}

function getObjectPath(slide = {}) {
  const slug = slide?.slug ? String(slide.slug) : '';
  if (slug) return `/realty/${encodeURIComponent(slug)}`;

  const id = slide?.id ?? '';
  if (id !== '' && id !== null && id !== undefined) {
    return `/realty/${encodeURIComponent(String(id))}`;
  }

  const fromLink = extractKeyFromLink(slide?.objectLink);
  if (fromLink) return `/realty/${encodeURIComponent(fromLink)}`;

  return '/search';
}

function toSlide(x){
  if (!x) return null;
  
  // Безопасное извлечение изображения
  let imageUrl = x.image || x.cover_url || x.cover_thumb;
  if (!imageUrl && x.images?.[0]) {
    const firstImg = x.images[0];
    if (typeof firstImg === 'object' && firstImg !== null) {
      imageUrl = firstImg.url || firstImg.thumbnail_url || firstImg.display_url || firstImg.src;
    } else if (typeof firstImg === 'string') {
      imageUrl = firstImg;
    }
  }
  
  return {
    id: x.id ?? x.slug ?? Math.random(),
    slug: x.slug,
    title: String(x.title || x.name || x.title_ru || x.title_en || 'Object'),
    benefits: String(x.benefits || x.subtitle || ''),
    image: imageUrl ? normalizeMediaUrl(imageUrl) : null,
    objectLink: x.link || x.href || null,
  };
}

export default function HeroSlider({
  items = [],
  autoplay = true,
  autoplayIntervalMs = 4000,   // Уменьшили с 30 сек до 4 сек
  pauseOnHover = true,
}){
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const slides = React.useMemo(
    () => (Array.isArray(items) ? items.map(toSlide).filter(Boolean) : []),
    [items]
  );

  const count = slides.length;
  const [index, setIndex] = React.useState(0);

  React.useEffect(() => { setIndex(0); }, [count]);

  const next = React.useCallback(() => {
    if (count < 2) return;
    setIndex(i => (i + 1) % count); // Циклический переход: после последнего -> первый
  }, [count]);

  const prev = React.useCallback(() => {
    if (count < 2) return;
    setIndex(i => (i - 1 + count) % count); // Циклический переход: перед первым -> последний
  }, [count]);

  const [hovered, setHovered] = React.useState(false);
  React.useEffect(() => {
    if (!autoplay || count < 2) return;
    if (pauseOnHover && hovered) return;
    // Убираем ограничение - теперь автоплей работает бесконечно
    const id = setInterval(next, Math.max(2000, autoplayIntervalMs));
    return () => clearInterval(id);
  }, [autoplay, autoplayIntervalMs, pauseOnHover, hovered, count, next]);

  const touch = React.useRef({x:0, y:0});
  const onTouchStart = (e) => { touch.current.x = e.touches[0].clientX; touch.current.y = e.touches[0].clientY; };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touch.current.x;
    const dy = e.changedTouches[0].clientY - touch.current.y;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 30) {
      dx < 0 ? next() : prev();
    }
  };

  const tx = `-${index * 100}%`;
  const cur = slides[index] || null;
  const title    = String(cur?.title || 'Object');
  const benefits = String(cur?.benefits || '');

  return (
    <Wrap
      aria-label="hero-slider"
      onMouseEnter={()=> pauseOnHover && setHovered(true)}
      onMouseLeave={()=> setHovered(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <Viewport>
        <Track $tx={tx} $anim>
          {slides.map((s, i) => (
            <Slide key={String(s.id ?? i)}>
              {s.image ? <img src={s.image} alt={s.title || 'slide'} /> : null}
              <SlideLink to={getObjectPath(s)} aria-label={s.title || 'Object'} />
            </Slide>
          ))}
        </Track>

        {cur && (
          <>
            <Caption>
              <h3>{safeRender(title, 'Object')}</h3>
              {benefits ? <p>{safeRender(benefits)}</p> : null}
            </Caption>

            <CtaBtn 
              type="button" 
              onClick={(e) => { 
                e.preventDefault(); 
                e.stopPropagation(); 
                setOpen(true); 
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              {t('learnMore')}
            </CtaBtn>
          </>
        )}

        {count > 1 && (
          <>
            <NavBtn $left onClick={prev} aria-label="Previous slide"><ArrowLeftIcon/></NavBtn>
            <NavBtn onClick={next} aria-label="Next slide"><ArrowRightIcon/></NavBtn>

            <Dots>
              {slides.map((_, i) => (
                <Dot
                  key={i}
                  $active={i === index}
                  aria-label={`Go to slide ${i+1}`}
                  onClick={()=>setIndex(i)}
                />
              ))}
            </Dots>
          </>
        )}
      </Viewport>

      <LeadModal open={open} onClose={()=>setOpen(false)} />
    </Wrap>
  );
}
